import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Bot, Save, ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

const AdminSettings = () => {
  const [mappings, setMappings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/roles/chatbot-mapping');
      setMappings(response.data);
    } catch (error) {
      console.error('Failed to fetch chatbot mappings:', error);
      toast.error('Failed to load role configurations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (roleId, currentStatus) => {
    setMappings(mappings.map(m => 
      m.role_id === roleId ? { ...m, is_enabled: !currentStatus } : m
    ));
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      
      // Update each mapping sequentially
      const promises = mappings.map(m => 
        api.put(`/roles/chatbot-mapping/${m.role_id}`, {
          is_enabled: m.is_enabled
        })
      );
      
      await Promise.all(promises);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">System Settings</h1>
        <p className="text-slate-500 dark:text-slate-400">Manage global application configurations</p>
      </div>

      <Card className="border-indigo-100 dark:border-indigo-900 shadow-sm">
        <CardHeader className="bg-slate-50 dark:bg-slate-800/50 border-b border-indigo-50 dark:border-indigo-900/50">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg text-indigo-600 dark:text-indigo-400">
              <Bot size={20} />
            </div>
            <div>
              <CardTitle className="text-lg">AI Assistant Configuration</CardTitle>
              <CardDescription>Manage AI chatbot availability for individual roles</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {mappings.length === 0 ? (
              <div className="p-4 bg-orange-50 border border-orange-200 text-orange-800 rounded-xl flex items-center gap-2">
                <ShieldAlert size={20} />
                <p>No roles found in the system. Please ensure the database is seeded.</p>
              </div>
            ) : (
              mappings.filter(m => m.role_name !== 'Admin').map((mapping) => (
                <div key={mapping.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm transition-all hover:border-indigo-300">
                  <div className="space-y-1">
                    <h3 className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                      {mapping.role_name}
                      {!mapping.is_enabled && <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full font-semibold">Disabled</span>}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Allow users with the <strong>{mapping.role_name}</strong> role to interact with the AI assistant.
                    </p>
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={mapping.is_enabled}
                        onChange={() => handleToggle(mapping.role_id, mapping.is_enabled)}
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <Button 
              onClick={saveSettings} 
              disabled={isSaving || mappings.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
            >
              <Save size={16} />
              {isSaving ? 'Saving...' : 'Save Configurations'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
